<template>
  <Button v-bind="$attrs" :label="label" :class="[
    props.class,
    {
      'p-invalid': isInvalid,
      'p-disabled': disabled || loading,
      'p-button-loading': loading,
      'p-button-sm': size === 'small',
      'p-button-lg': size === 'large',
      [`p-button-${severity}`]: severity && severity !== 'primary',
      'p-button-rounded': rounded,
      'p-button-text': text,
      'p-button-outlined': outlined,
      'p-button-raised': raised
    }
  ]" :disabled="disabled || loading" :icon="icon" :loading="loading" :iconPos="iconPos" role="button" :type="type"
    :aria-label="ariaLabel || label" :aria-disabled="disabled || loading ? 'true' : undefined"
    :aria-invalid="isInvalid ? 'true' : undefined" :aria-required="required ? 'true' : undefined"
    :aria-haspopup="props['aria-haspopup']" :aria-expanded="props['aria-expanded']" :dir="isRTL ? 'rtl' : 'ltr'"
    tabindex="0" :pt="{
      root: { class: rootClass },
      label: { class: labelClass },
      icon: { class: iconClass },
      loadingIcon: { class: loadingIconClass }
    }" @click="onClick" @focus="onFocus" @blur="onBlur" @keydown="onKeyDown">
    <slot></slot>
  </Button>
  <small v-if="isInvalid && displayErrorMessage" class="p-error" role="alert">
    {{ t(displayErrorMessage) }}
  </small>
</template>

<script setup lang="ts">
import Button from 'primevue/button';
import type { ButtonProps, ButtonEmits } from './types';
import { useForm } from '../../../../composables/useForm';
import { useLocalization } from '../../../../composables/useLocalization';
import { computed, ref } from 'vue';

const props = withDefaults(defineProps<ButtonProps>(), {
  severity: 'primary',
  disabled: false,
  iconPos: 'left',
  loading: false,
  rounded: false,
  text: false,
  outlined: false,
  raised: false,
  required: false,
  invalid: false,
  errorMessage: '',
  ariaLabel: '',
  type: 'button' as const,
  size: undefined
});

const emit = defineEmits<ButtonEmits>();

interface FormValues {
  [key: string]: unknown;
}

// Form integration
const form = useForm<FormValues>({ [props.name || '']: undefined });
const errors = computed(() => form.errors.value);
const touched = computed(() => form.touched.value);

// Localization
const { isRTL, t } = useLocalization();

// State management
const focused = ref(false);

// Computed properties
const isInvalid = computed(() => {
  if (!props.name) return props.invalid;
  return props.invalid || (errors.value[props.name]?.length > 0);
});

const displayErrorMessage = computed(() => {
  if (!props.name) return props.errorMessage;
  return props.errorMessage || errors.value[props.name]?.[0];
});

const rootClass = computed(() => [
  {
    'p-button-sm': props.size === 'small',
    'p-button-lg': props.size === 'large',
    [`p-button-${props.severity}`]: props.severity && props.severity !== 'primary',
    'p-button-rounded': props.rounded,
    'p-button-text': props.text,
    'p-button-outlined': props.outlined,
    'p-button-raised': props.raised,
    'p-disabled': props.disabled || props.loading,
    'p-button-loading': props.loading,
    'p-invalid': isInvalid.value
  }
]);

const labelClass = computed(() => [
  'p-button-label',
  {
    'p-button-label-icon-only': !props.label && props.icon,
    'p-button-label-sm': props.size === 'small',
    'p-button-label-lg': props.size === 'large'
  }
]);

const iconClass = computed(() => [
  'p-button-icon',
  {
    'p-button-icon-left': props.iconPos === 'left' && props.label,
    'p-button-icon-right': props.iconPos === 'right' && props.label,
    'p-button-icon-sm': props.size === 'small',
    'p-button-icon-lg': props.size === 'large'
  },
  props.icon
]);

const loadingIconClass = computed(() => [
  'p-button-loading-icon pi pi-spinner pi-spin',
  {
    'p-button-loading-icon-sm': props.size === 'small',
    'p-button-loading-icon-lg': props.size === 'large'
  }
]);

// Event handlers
const onClick = (event: MouseEvent) => {
  if (!props.disabled && !props.loading) {
    emit('click', event);
    if (props.name) {
      form.setFieldTouched(props.name as keyof FormValues, true);
    }
  }
};

const onFocus = (event: FocusEvent) => {
  focused.value = true;
  emit('focus', event);
  if (props.name) {
    form.setFieldTouched(props.name as keyof FormValues, true);
  }
};

const onBlur = (event: FocusEvent) => {
  focused.value = false;
  emit('blur', event);
  if (props.name) {
    form.setFieldTouched(props.name as keyof FormValues, true);
  }
};

const onKeyDown = (event: KeyboardEvent) => {
  if (!props.disabled && !props.loading) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick(event as unknown as MouseEvent);
    }
  }
};
</script>
